import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../store";
import { logout } from "../../store/slices/authSlice";
import { useGetUserMembershipStatusQuery, useGetProfileQuery, useGetUserWalletBalanceQuery } from "../../store/slices/apiSlice";
import toast from "react-hot-toast";
import { API_BASE_URL } from "../../config/api";
import {
    ChevronDown,
    Shield,
    Users,
    Globe,
    TrendingUp,
    User,
    Wallet,
    LogOut,
    Store,
    Home,
    Heart,
    Briefcase,
    MessageSquare,
    Mail,
    Info,
    Sun,
    Building2
} from "lucide-react";
import logo from "../../assets/DWBRANDprview.png";
import UserAvatar from "./UserAvatar";

const investmentLinks = [
    { name: "Investment Overview", path: "/investment", icon: TrendingUp, desc: "Explore ROI & strategy" },
    { name: "Farm Sponsorship", path: "/farm-sponsorship", icon: Sun, desc: "Sponsor farm projects & harvest" },
    { name: "Real Estate Nigeria", path: "/investment/real-estate-investment-nigeria", icon: Building2, desc: "Prime property investments" },
    { name: "Investing From Abroad", path: "/investment/real-estate-investing-from-abroad", icon: Globe, desc: "Seamless remote investing" },
    { name: "Commercial Property", path: "/investment/commercial-property-investment-nigeria", icon: Building2, desc: "High yield commercial deals" },
    { name: "Fractional Investment", path: "/investment/fractional-property-investment-nigeria", icon: TrendingUp, desc: "Co-own premium properties" },
];

const diasporaLinks = [
    { name: "Diaspora Overview", path: "/nigerian-diaspora-investors", icon: Users, desc: "Tailored diaspora solutions" },
    { name: "United Kingdom", path: "/nigerian-diaspora-investors/uk", icon: Globe, desc: "UK Diaspora hub" },
    { name: "United States", path: "/nigerian-diaspora-investors/us", icon: Globe, desc: "US Diaspora hub" },
    { name: "Europe", path: "/nigerian-diaspora-investors/europe", icon: Globe, desc: "European investors" },
    { name: "Asia", path: "/nigerian-diaspora-investors/asia", icon: Globe, desc: "Asian region investors" },
];

const companyLinks = [
    { name: "About Us", path: "/about", icon: Info, desc: "Our story, mission & vision" },
    { name: "Blogs & Insights", path: "/blogs", icon: MessageSquare, desc: "Latest market news & updates" },
    { name: "Careers", path: "/careers", icon: Briefcase, desc: "Join our global team" },
    { name: "Contact Us", path: "/contact", icon: Mail, desc: "Get in touch with support" },
];

const Navbar = () => {
    const [menuOpen, setMenuOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [investDiasporaOpen, setInvestDiasporaOpen] = useState(false);
    const [companyOpen, setCompanyOpen] = useState(false);
    const [userMenuOpen, setUserMenuOpen] = useState(false);

    // Mobile dropdown accordion state
    const [mobileAccordion, setMobileAccordion] = useState<string | null>(null);

    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { user } = useSelector((state: RootState) => state.auth);
    const isActive = (path: string) => location.pathname === path;

    // Active state checkers for dropdowns
    const isInvestDiasporaActive = 
        location.pathname.startsWith('/investment') || 
        location.pathname.startsWith('/nigerian-diaspora-investors');

    const isCompanyActive = 
        ['/about', '/blogs', '/careers', '/contact'].some(path => location.pathname.startsWith(path));

    const isProjectDetailPage = location.pathname.startsWith('/projects/') && location.pathname !== '/projects';

    const isRegularUser = user?.role === 'USER' || user?.role === 'DEALER';
    const isDealer = user?.role === 'DEALER';

    // Check membership status for regular users
    const { data: membershipStatusResponse } = useGetUserMembershipStatusQuery(undefined, {
        skip: !isRegularUser,
    });



    // Fetch user wallet balance
    const { data: walletData } = useGetUserWalletBalanceQuery(undefined, {
        skip: !isRegularUser,
    });
    const navbarWalletBalance = walletData?.data?.wallet_balance || 0;
    // API returns currency_symbol directly (e.g. ₹, $, £) — use it directly
    const navbarCurrencySymbol = walletData?.data?.currency_symbol || '$';

    // Fetch profile data for profile image
    const { data: profileData } = useGetProfileQuery(
        { role: user?.role || 'USER' },
        { skip: !user }
    );
    const profileImage = profileData?.data?.profile_image;

    const getProfileImageUrl = (): string | null => {
        if (!profileImage) return null;
        if (profileImage.startsWith('http')) return profileImage;
        return `${API_BASE_URL}/uploads/${profileImage}`;
    };
    const profileImageUrl = getProfileImageUrl();

    const handleLogout = () => {
        dispatch(logout());
        toast.success('Logged out successfully!');
        navigate('/');
        setUserMenuOpen(false);
    };

    const getUserProfilePath = () => user?.role === 'DEALER' ? '/dealer/profile' : '/user/profile';
    const getUserWalletPath = () => user?.role === 'DEALER' ? '/dealer/wallet' : '/user/wallet';
    const getUserMarketplacePath = () => '/marketplace';

    const toggleMobileAccordion = (key: string) => {
        setMobileAccordion(mobileAccordion === key ? null : key);
    };

    const closeMobileMenu = () => {
        setMenuOpen(false);
        setMobileAccordion(null);
    };

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 20);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        setMenuOpen(false);
        setMobileAccordion(null);
        setUserMenuOpen(false);
        setInvestDiasporaOpen(false);
        setCompanyOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            if (userMenuOpen && !target.closest('.user-menu-container')) {
                setUserMenuOpen(false);
            }
        };
        if (userMenuOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [userMenuOpen]);

    return (
        <>
            {/* Mobile Menu Overlay */}
            {menuOpen && (
                <div
                    className="fixed inset-0 bg-black/30 backdrop-blur-xs z-40 lg:hidden"
                    onClick={closeMobileMenu}
                    aria-hidden="true"
                />
            )}

            <nav
                className={`sticky top-0 z-50 w-full transition-all duration-300 ease-out backdrop-blur-md
                    ${scrolled
                        ? 'bg-white/95 shadow-md border-b border-gray-100'
                        : 'bg-white/90 shadow-xs'
                    }`}
                aria-label="Main Navigation"
            >
                <div className="container mx-auto flex items-center justify-between px-4 py-2.5 lg:py-3">
                    {/* Logo & Brand */}
                    <Link
                        to="/"
                        className="flex items-center gap-2.5 group cursor-pointer shrink-0"
                        aria-label="Home"
                    >
                        <div className="w-10 h-8 sm:w-11 sm:h-9 lg:w-12 lg:h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 shadow-md flex items-center justify-center group-hover:shadow-lg transition-all duration-200 group-hover:scale-105">
                            <img
                                src={logo}
                                alt="Diasporawise Logo"
                                className="h-5 w-6 sm:h-6 sm:w-7 group-hover:scale-110 transition-transform duration-200"
                            />
                        </div>

                        <div className="min-w-0">
                            <h3 className="text-base sm:text-lg font-bold tracking-wide text-gray-900"
                                style={{ fontFamily: 'Montserrat, Arial, sans-serif' }}>
                                <span>DIASPORA</span>
                                <span className="text-blue-600">WISE</span>
                            </h3>
                            <p className="text-[10px] text-gray-500 font-medium tracking-tight -mt-1 hidden sm:block">Investment Platform</p>
                        </div>
                    </Link>

                    {/* Streamlined Desktop Navigation */}
                    <div className="hidden lg:flex items-center gap-1.5 font-medium">
                        {/* 1. Home */}
                        <Link
                            to="/"
                            className={`px-3.5 py-2 text-sm rounded-lg transition-all duration-200 ${
                                isActive('/')
                                    ? 'text-blue-600 font-semibold bg-blue-50/80 shadow-xs'
                                    : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'
                            }`}
                        >
                            Home
                        </Link>

                        {/* 2. Projects */}
                        <Link
                            to="/projects"
                            className={`px-3.5 py-2 text-sm rounded-lg transition-all duration-200 ${
                                isActive('/projects')
                                    ? 'text-blue-600 font-semibold bg-blue-50/80 shadow-xs'
                                    : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'
                            }`}
                        >
                            Projects
                        </Link>

                        {/* 3. Farm Sponsorship */}
                        <Link
                            to="/farm-sponsorship"
                            className={`px-3.5 py-2 text-sm rounded-lg flex items-center gap-1.5 transition-all duration-200 ${
                                isActive('/farm-sponsorship')
                                    ? 'text-blue-600 font-semibold bg-blue-50/80 shadow-xs'
                                    : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'
                            }`}
                        >
                            <Sun className="w-4 h-4 text-amber-500" />
                            <span>Farm Sponsorship</span>
                        </Link>

                        {/* 3. Investments & Diaspora Dropdown */}
                        <div 
                            className="relative"
                            onMouseEnter={() => setInvestDiasporaOpen(true)}
                            onMouseLeave={() => setInvestDiasporaOpen(false)}
                        >
                            <button
                                className={`px-3.5 py-2 text-sm rounded-lg flex items-center gap-1.5 transition-all duration-200 ${
                                    isInvestDiasporaActive
                                        ? 'text-blue-600 font-semibold bg-blue-50/80 shadow-xs'
                                        : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'
                                }`}
                            >
                                <TrendingUp className="w-4 h-4 text-blue-500" />
                                <span>Investments & Diaspora</span>
                                <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-300 ${investDiasporaOpen ? 'rotate-180 text-blue-600' : 'text-gray-400'}`} />
                            </button>

                            {/* Rich Mega-Dropdown */}
                            <div
                                className={`absolute top-full left-1/2 -translate-x-1/2 mt-1.5 w-[540px] bg-white rounded-2xl shadow-xl border border-gray-100 p-4 grid grid-cols-2 gap-4 transition-all duration-200 z-50 ${
                                    investDiasporaOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'
                                }`}
                            >
                                {/* Column 1: Investment Guides */}
                                <div className="space-y-1">
                                    <div className="px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-blue-600 border-b border-blue-50/80 mb-1">
                                        Investment Solutions
                                    </div>
                                    {investmentLinks.map((item) => (
                                        <Link
                                            key={item.path}
                                            to={item.path}
                                            className={`flex items-start gap-2.5 p-2 rounded-xl transition-all duration-200 ${
                                                isActive(item.path)
                                                    ? 'bg-blue-50/90 text-blue-700 font-semibold'
                                                    : 'hover:bg-gray-50 text-gray-700 hover:text-blue-600'
                                            }`}
                                        >
                                            <item.icon className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />
                                            <div>
                                                <div className="text-xs font-semibold leading-snug">{item.name}</div>
                                                <div className="text-[10px] text-gray-500 line-clamp-1">{item.desc}</div>
                                            </div>
                                        </Link>
                                    ))}
                                </div>

                                {/* Column 2: Diaspora Portals */}
                                <div className="space-y-1 bg-gray-50/50 p-2 rounded-xl border border-gray-100/80">
                                    <div className="px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-blue-600 border-b border-blue-100/60 mb-1">
                                        Diaspora Portals
                                    </div>
                                    {diasporaLinks.map((item) => (
                                        <Link
                                            key={item.path}
                                            to={item.path}
                                            className={`flex items-start gap-2.5 p-2 rounded-xl transition-all duration-200 ${
                                                isActive(item.path)
                                                    ? 'bg-blue-600 text-white font-semibold shadow-xs'
                                                    : 'hover:bg-white text-gray-700 hover:text-blue-600 shadow-2xs'
                                            }`}
                                        >
                                            <item.icon className={`w-4 h-4 mt-0.5 shrink-0 ${isActive(item.path) ? 'text-white' : 'text-blue-500'}`} />
                                            <div>
                                                <div className="text-xs font-semibold leading-snug">{item.name}</div>
                                                <div className={`text-[10px] line-clamp-1 ${isActive(item.path) ? 'text-blue-100' : 'text-gray-500'}`}>{item.desc}</div>
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* 4. Marketplace (Hidden for dealers) */}
                        {!isDealer && (
                            <Link
                                to="/marketplace"
                                className={`px-3.5 py-2 text-sm rounded-lg flex items-center gap-1.5 transition-all duration-200 ${
                                    isActive('/marketplace')
                                        ? 'text-blue-600 font-semibold bg-blue-50/80 shadow-xs'
                                        : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'
                                }`}
                            >
                                <Store className="w-4 h-4 text-gray-400" />
                                Marketplace
                            </Link>
                        )}

                        {/* 5. Stay (For logged-in users) */}
                        {isRegularUser && !isDealer && (
                            <Link
                                to="/apartments"
                                className={`px-3.5 py-2 text-sm rounded-lg flex items-center gap-1.5 transition-all duration-200 ${
                                    location.pathname.startsWith('/apartments')
                                        ? 'text-blue-600 font-semibold bg-blue-50/80 shadow-xs'
                                        : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'
                                }`}
                            >
                                <Home className="w-4 h-4 text-gray-400" />
                                Stay
                            </Link>
                        )}

                        {/* 6. Solar */}
                        <a
                            href="/solar"
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`px-3.5 py-2 text-sm rounded-lg flex items-center gap-1.5 transition-all duration-200 ${
                                isActive('/solar')
                                    ? 'text-blue-600 font-semibold bg-blue-50/80 shadow-xs'
                                    : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'
                            }`}
                        >
                            <Sun className="w-4 h-4 text-amber-500" />
                            Solar
                        </a>

                        {/* 7. Company Dropdown */}
                        <div 
                            className="relative"
                            onMouseEnter={() => setCompanyOpen(true)}
                            onMouseLeave={() => setCompanyOpen(false)}
                        >
                            <button
                                className={`px-3.5 py-2 text-sm rounded-lg flex items-center gap-1.5 transition-all duration-200 ${
                                    isCompanyActive
                                        ? 'text-blue-600 font-semibold bg-blue-50/80 shadow-xs'
                                        : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'
                                }`}
                            >
                                <span>Company</span>
                                <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-300 ${companyOpen ? 'rotate-180 text-blue-600' : 'text-gray-400'}`} />
                            </button>

                            {/* Dropdown Menu */}
                            <div
                                className={`absolute top-full right-0 mt-1.5 w-64 bg-white rounded-2xl shadow-xl border border-gray-100 p-2 space-y-1 transition-all duration-200 z-50 ${
                                    companyOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'
                                }`}
                            >
                                {companyLinks.map((item) => (
                                    <Link
                                        key={item.path}
                                        to={item.path}
                                        className={`flex items-start gap-3 p-2.5 rounded-xl transition-all duration-200 ${
                                            isActive(item.path)
                                                ? 'bg-blue-50/90 text-blue-700 font-semibold'
                                                : 'hover:bg-gray-50 text-gray-700 hover:text-blue-600'
                                        }`}
                                    >
                                        <item.icon className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />
                                        <div>
                                            <div className="text-xs font-semibold">{item.name}</div>
                                            <div className="text-[10px] text-gray-500 leading-tight">{item.desc}</div>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Right Side Actions */}
                    <div className="hidden lg:flex items-center gap-3">
                        {user ? (
                            isRegularUser ? (
                                <div className="flex items-center gap-2.5">
                                    <Link
                                        to={getUserWalletPath()}
                                        className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 rounded-xl text-xs font-extrabold text-emerald-800 transition-all shadow-2xs"
                                        title="Account Wallet Balance"
                                    >
                                        <Wallet className="w-3.5 h-3.5 text-emerald-600" />
                                        <span>{navbarCurrencySymbol}{navbarWalletBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                    </Link>

                                    <div className="relative user-menu-container">
                                        <button
                                            onClick={() => setUserMenuOpen(!userMenuOpen)}
                                            className="text-sm font-medium px-3.5 py-1.5 bg-white border border-gray-200 rounded-xl shadow-xs hover:shadow-md transition-all duration-200 flex items-center gap-2.5 group"
                                        >
                                        <UserAvatar 
                                            src={profileImageUrl} 
                                            name={user.name} 
                                            size="sm" 
                                        />
                                        <span className="font-semibold text-gray-900">{user.name || user.email?.split('@')[0] || 'User'}</span>
                                        <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${userMenuOpen ? 'rotate-180' : ''}`} />
                                    </button>

                                    {userMenuOpen && (
                                        <div className="absolute right-0 mt-2 w-52 bg-white rounded-2xl shadow-xl border border-gray-100 p-2 overflow-hidden z-50">
                                            {user?.role === 'DEALER' ? (
                                                <>
                                                    <Link
                                                        to="/dealer/dashboard"
                                                        onClick={() => setUserMenuOpen(false)}
                                                        className="flex items-center gap-3 px-3.5 py-2.5 text-xs font-medium text-gray-700 hover:bg-gray-50 rounded-xl transition-colors"
                                                    >
                                                        <Home className="w-4 h-4 text-blue-500" />
                                                        <span>Dashboard</span>
                                                    </Link>
                                                    <Link
                                                        to={getUserProfilePath()}
                                                        onClick={() => setUserMenuOpen(false)}
                                                        className="flex items-center gap-3 px-3.5 py-2.5 text-xs font-medium text-gray-700 hover:bg-gray-50 rounded-xl transition-colors"
                                                    >
                                                        <User className="w-4 h-4 text-gray-400" />
                                                        <span>Profile</span>
                                                    </Link>
                                                    <div className="my-1 border-t border-gray-100"></div>
                                                    <button
                                                        onClick={handleLogout}
                                                        className="w-full flex items-center gap-3 px-3.5 py-2.5 text-xs font-medium text-red-600 hover:bg-red-50 rounded-xl transition-colors"
                                                    >
                                                        <LogOut className="w-4 h-4 text-red-500" />
                                                        <span>Sign Out</span>
                                                    </button>
                                                </>
                                            ) : (
                                                <>
                                                    <Link
                                                        to={getUserProfilePath()}
                                                        onClick={() => setUserMenuOpen(false)}
                                                        className="flex items-center gap-3 px-3.5 py-2.5 text-xs font-medium text-gray-700 hover:bg-gray-50 rounded-xl transition-colors"
                                                    >
                                                        <User className="w-4 h-4 text-blue-500" />
                                                        <span>Profile</span>
                                                    </Link>
                                                    <Link
                                                        to={getUserWalletPath()}
                                                        onClick={() => setUserMenuOpen(false)}
                                                        className="flex items-center gap-3 px-3.5 py-2.5 text-xs font-medium text-gray-700 hover:bg-gray-50 rounded-xl transition-colors"
                                                    >
                                                        <Wallet className="w-4 h-4 text-amber-500" />
                                                        <span>Wallet</span>
                                                    </Link>
                                                    <Link
                                                        to={getUserMarketplacePath()}
                                                        onClick={() => setUserMenuOpen(false)}
                                                        className="flex items-center gap-3 px-3.5 py-2.5 text-xs font-medium text-gray-700 hover:bg-gray-50 rounded-xl transition-colors"
                                                    >
                                                        <Store className="w-4 h-4 text-purple-500" />
                                                        <span>Marketplace</span>
                                                    </Link>
                                                    <Link
                                                        to="/apartments"
                                                        onClick={() => setUserMenuOpen(false)}
                                                        className="flex items-center gap-3 px-3.5 py-2.5 text-xs font-medium text-gray-700 hover:bg-gray-50 rounded-xl transition-colors"
                                                    >
                                                        <Home className="w-4 h-4 text-emerald-500" />
                                                        <span>Stay</span>
                                                    </Link>

                                                    <Link
                                                        to="/farm-sponsorship"
                                                        onClick={() => setUserMenuOpen(false)}
                                                        className="flex items-center gap-3 px-3.5 py-2.5 text-xs font-medium text-gray-700 hover:bg-gray-50 rounded-xl transition-colors"
                                                    >
                                                        <Sun className="w-4 h-4 text-amber-500" />
                                                        <span>Farm Sponsorship</span>
                                                    </Link>
                                                    <div className="my-1 border-t border-gray-100"></div>
                                                    <button
                                                        onClick={handleLogout}
                                                        className="w-full flex items-center gap-3 px-3.5 py-2.5 text-xs font-medium text-red-600 hover:bg-red-50 rounded-xl transition-colors"
                                                    >
                                                        <LogOut className="w-4 h-4 text-red-500" />
                                                        <span>Sign Out</span>
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                            ) : (
                                <Link
                                    to="/admin"
                                    className="text-xs font-semibold px-4 py-2 bg-gray-900 hover:bg-gray-800 text-white rounded-xl shadow-xs hover:shadow-md transition-all duration-200 flex items-center gap-1.5"
                                >
                                    <Shield className="w-4 h-4 text-blue-400" />
                                    <span>Admin Panel</span>
                                </Link>
                            )
                        ) : (
                            !isProjectDetailPage && (
                                <a
                                    href="https://app.diasporawise.com"
                                    className="text-xs font-semibold px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl shadow-sm hover:shadow-md transition-all duration-200 flex items-center gap-1.5"
                                >
                                    <User className="w-4 h-4" />
                                    <span>Sign In</span>
                                </a>
                            )
                        )}
                    </div>

                    {/* Mobile Hamburger Menu Toggle */}
                    <button
                        className="lg:hidden p-2 rounded-xl text-gray-700 hover:bg-gray-100 transition-colors focus:outline-none"
                        aria-label={menuOpen ? "Close menu" : "Open menu"}
                        onClick={() => setMenuOpen(!menuOpen)}
                    >
                        <div className="w-5 h-5 flex flex-col justify-center items-center relative">
                            <span className={`block w-5 h-0.5 bg-gray-700 rounded-full transition-all duration-300 transform ${menuOpen ? 'rotate-45 translate-y-0.5' : '-translate-y-1'}`}></span>
                            <span className={`block w-5 h-0.5 bg-gray-700 rounded-full transition-all duration-300 ${menuOpen ? 'opacity-0' : 'opacity-100'}`}></span>
                            <span className={`block w-5 h-0.5 bg-gray-700 rounded-full transition-all duration-300 transform ${menuOpen ? '-rotate-45 -translate-y-0.5' : 'translate-y-1'}`}></span>
                        </div>
                    </button>
                </div>

                {/* Mobile Menu Content */}
                <div
                    className={`lg:hidden bg-white px-4 pb-6 overflow-hidden transition-all duration-300 border-t border-gray-100 ${
                        menuOpen ? "max-h-[85vh] overflow-y-auto opacity-100 py-4" : "max-h-0 opacity-0 py-0 pointer-events-none"
                    }`}
                >
                    <div className="space-y-1.5">
                        {/* 1. Home */}
                        <Link
                            to="/"
                            onClick={closeMobileMenu}
                            className={`block py-3 px-4 rounded-xl text-sm font-semibold transition-colors ${
                                isActive('/') ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-50'
                            }`}
                        >
                            Home
                        </Link>

                        {/* 2. Projects */}
                        <Link
                            to="/projects"
                            onClick={closeMobileMenu}
                            className={`block py-3 px-4 rounded-xl text-sm font-semibold transition-colors ${
                                isActive('/projects') ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-50'
                            }`}
                        >
                            Projects
                        </Link>

                        {/* 3. Farm Sponsorship */}
                        <Link
                            to="/farm-sponsorship"
                            onClick={closeMobileMenu}
                            className={`block py-3 px-4 rounded-xl text-sm font-semibold transition-colors flex items-center gap-2 ${
                                isActive('/farm-sponsorship') ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-50'
                            }`}
                        >
                            <Sun className="w-4 h-4 text-amber-500" />
                            <span>Farm Sponsorship</span>
                        </Link>

                        {/* 3. Investments & Diaspora Accordion */}
                        <div>
                            <button
                                onClick={() => toggleMobileAccordion('investDiaspora')}
                                className={`w-full flex items-center justify-between py-3 px-4 rounded-xl text-sm font-semibold transition-colors ${
                                    isInvestDiasporaActive ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-50'
                                }`}
                            >
                                <div className="flex items-center gap-2">
                                    <TrendingUp className="w-4 h-4 text-blue-500" />
                                    <span>Investments & Diaspora</span>
                                </div>
                                <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${mobileAccordion === 'investDiaspora' ? 'rotate-180' : ''}`} />
                            </button>

                            {mobileAccordion === 'investDiaspora' && (
                                <div className="ml-4 mt-1 pl-3 border-l-2 border-blue-200 space-y-1 py-1">
                                    <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider px-3 py-1">Investments</div>
                                    {investmentLinks.map((link) => (
                                        <Link
                                            key={link.path}
                                            to={link.path}
                                            onClick={closeMobileMenu}
                                            className={`block py-2 px-3 rounded-lg text-xs font-medium ${
                                                isActive(link.path) ? 'text-blue-600 bg-blue-50/50 font-bold' : 'text-gray-600 hover:text-blue-600'
                                            }`}
                                        >
                                            {link.name}
                                        </Link>
                                    ))}
                                    <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider px-3 py-1 pt-2">Diaspora Portals</div>
                                    {diasporaLinks.map((link) => (
                                        <Link
                                            key={link.path}
                                            to={link.path}
                                            onClick={closeMobileMenu}
                                            className={`block py-2 px-3 rounded-lg text-xs font-medium ${
                                                isActive(link.path) ? 'text-blue-600 bg-blue-50/50 font-bold' : 'text-gray-600 hover:text-blue-600'
                                            }`}
                                        >
                                            {link.name}
                                        </Link>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* 4. Marketplace */}
                        {!isDealer && (
                            <Link
                                to="/marketplace"
                                onClick={closeMobileMenu}
                                className={`block py-3 px-4 rounded-xl text-sm font-semibold transition-colors ${
                                    isActive('/marketplace') ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-50'
                                }`}
                            >
                                Marketplace
                            </Link>
                        )}

                        {/* 5. Stay */}
                        {isRegularUser && !isDealer && (
                            <Link
                                to="/apartments"
                                onClick={closeMobileMenu}
                                className={`block py-3 px-4 rounded-xl text-sm font-semibold transition-colors ${
                                    location.pathname.startsWith('/apartments') ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-50'
                                }`}
                            >
                                Stay (Short Rentals)
                            </Link>
                        )}

                        {/* 6. Solar */}
                        <a
                            href="/solar"
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={closeMobileMenu}
                            className={`block py-3 px-4 rounded-xl text-sm font-semibold transition-colors ${
                                isActive('/solar') ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-50'
                            }`}
                        >
                            Solar Energy
                        </a>

                        {/* 7. Company Accordion */}
                        <div>
                            <button
                                onClick={() => toggleMobileAccordion('company')}
                                className={`w-full flex items-center justify-between py-3 px-4 rounded-xl text-sm font-semibold transition-colors ${
                                    isCompanyActive ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-50'
                                }`}
                            >
                                <div className="flex items-center gap-2">
                                    <Info className="w-4 h-4 text-blue-500" />
                                    <span>Company</span>
                                </div>
                                <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${mobileAccordion === 'company' ? 'rotate-180' : ''}`} />
                            </button>

                            {mobileAccordion === 'company' && (
                                <div className="ml-4 mt-1 pl-3 border-l-2 border-blue-200 space-y-1 py-1">
                                    {companyLinks.map((link) => (
                                        <Link
                                            key={link.path}
                                            to={link.path}
                                            onClick={closeMobileMenu}
                                            className={`block py-2 px-3 rounded-lg text-xs font-medium ${
                                                isActive(link.path) ? 'text-blue-600 bg-blue-50/50 font-bold' : 'text-gray-600 hover:text-blue-600'
                                            }`}
                                        >
                                            {link.name}
                                        </Link>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Mobile Auth Button */}
                        <div className="pt-4 border-t border-gray-100">
                            {user ? (
                                <button
                                    onClick={() => {
                                        handleLogout();
                                        closeMobileMenu();
                                    }}
                                    className="w-full py-3 px-4 text-center text-sm font-semibold text-red-600 bg-red-50 rounded-xl hover:bg-red-100 transition-colors"
                                >
                                    Sign Out
                                </button>
                            ) : (
                                <a
                                    href="https://app.diasporawise.com"
                                    className="block w-full py-3 px-4 text-center text-sm font-semibold text-white bg-blue-600 rounded-xl hover:bg-blue-700 shadow-sm transition-colors"
                                >
                                    Sign In
                                </a>
                            )}
                        </div>
                    </div>
                </div>
            </nav>
        </>
    );
};

export default Navbar;